import { Box, Typography, styled, alpha } from "@mui/material";
import { getCategoryColor, getCategoryTitle } from "../utils";
import QuickInsightCategoryIcon from "./QuickInsightCategoryIcon";
import { ApiQuickInsightCategory, ApiQuickInsightResult } from "@incendium/api";
import { Fragment, useMemo } from "react";

const StyledBox = styled(Box)<{
  category: ApiQuickInsightCategory;
  width: number | string;
}>(({ theme, category, width }) => ({
  background: `linear-gradient(135deg, ${alpha(getCategoryColor(category), 0.8)} 0%, ${getCategoryColor(category)} 100%)`,
  borderRadius: theme.shape.borderRadius,
  position: "relative",
  width: width || 300,
  height: 245,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(3),
  color: theme.palette.common.white,
}));

interface IQuickInsightCardProps {
  insight: ApiQuickInsightResult;
  width: number | string;
}

const renderLine = (line: string) => {
  const parts: React.ReactNode[] = [];
  const regex = /\[\[(.*?)\]\]/g;

  let lastIndex = 0;

  for (const match of line.matchAll(regex)) {
    // Text before the match
    if (match.index && match.index > lastIndex) {
      parts.push(line.slice(lastIndex, match.index));
    }

    // Highlighted part
    parts.push(
      <Typography
        key={match.index}
        variant="subtitle1"
        color="white"
        display="inline"
      >
        {match[1]}
      </Typography>,
    );

    lastIndex = match.index ? match.index + match[0].length : lastIndex;
  }

  // Remaining text
  if (lastIndex < line.length) {
    parts.push(line.slice(lastIndex));
  }

  return parts;
};

function QuickInsightCard({ insight, width }: IQuickInsightCardProps) {
  const result = useMemo(() => {
    const t = insight.result || "";
    const title = t.match(/\[([^\]]*)\]/);
    if (!title) {
      return null;
    }
    const rest = t.match(/^\[[^\]]*\](.*)$/s);
    const parsedRest = rest?.[1]?.split("\\n").map((line, i) => (
      <Fragment key={i}>
        {renderLine(line)}
        <br />
      </Fragment>
    ));
    return (
      <>
        <Typography
          variant="h2"
          color="white"
          lineHeight={1.5}
          mb={2}
          sx={{ textWrap: "balance" }}
        >
          {(title?.[1] || "").split("\\n").map((line, index) => (
            <Fragment key={index}>
              {line}
              <br />
            </Fragment>
          ))}
        </Typography>
        <Typography variant="body1" color="white" lineHeight={1.8}>
          {parsedRest}
        </Typography>
      </>
    );
  }, [insight.result]);

  return (
    <StyledBox
      category={insight.category as ApiQuickInsightCategory}
      width={width}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
        }}
      >
        <QuickInsightCategoryIcon
          category={insight.category as ApiQuickInsightCategory}
          fontSize="large"
          fill="white"
          color="inherit"
        />
      </Box>
      <Typography
        variant="body2"
        color="white"
        textTransform="uppercase"
        mb={2}
      >
        {getCategoryTitle(insight.category as ApiQuickInsightCategory)}
      </Typography>
      {result}
    </StyledBox>
  );
}

export default QuickInsightCard;
